import React, { useContext, useEffect, useRef, useState } from "react";
import "./style.css";
import { Breadcrumb } from "../../Components/Atoms";
import PATHS from "../../Routes/paths";
import { ClientPicker } from "../../Components/Molecules";
import { Autocomplete, Circle, DrawingManager, GoogleMap, InfoWindow, Marker, useJsApiLoader } from "@react-google-maps/api";
import { GeoFenceIcon, SearchIcon } from "../../Assets/images/svgs";
import { Button, Checkbox, Input, Tooltip, useToast } from "@chakra-ui/react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { addNewGeofence, getGeofencesByClient, updateGeofence, saveAlert, updateAlert, getAlerts } from "../../APIs";
import { StoreContext } from "../../Library/contexts";
import { GEOFENCE_TRIGGER_TYPE, GOOGLE_MAPS_API_KEY } from "../../Library/constants";
import { ALERT_VEHICLE_TABLE_HEADERS } from "../../Library/tableHeaders";
import { isEqual } from "lodash";
import { AlertsVehiclesTable } from "../../Components/Organisms";

const AddGeofence = () => {
  const libraries = [ "places", "drawing" ];
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });
  const [fromAlerts, setFromAlerts] = useState(true)

  const { filters, userVehicles, allGeofences, setAllGeofences } = useContext(StoreContext);

  const Toast = useToast();
  const [ searchParams ] = useSearchParams();
  const geofenceId = useRef(searchParams.get("idx"));

  const navigate = useNavigate();

  const isEdit = useRef(false);

  const mapRef = useRef();
  const circleRef = useRef();
  const setMapCircleRef = useRef();
  const autocompleteRef = useRef();

  const editedGeofenceRef = useRef();

  const breadcrumbPaths = [
    { idx: 1, name: "Lease", link: PATHS.LEASE },
    { idx: 2, name: "Vehicles", link: PATHS.LEASED_VEHICLES },
    { idx: 2, name: "Add Geofence", isCurrentPage: true },
  ];

  const fromAlertBreadcrumbPaths = [
    {
      idx: 1,
      name: "Geofence Alerts",
      link: `${PATHS.SET_ALERTS}?tab=${1}`,
    },
    { idx: 2, name: isEdit.current ? "Edit Geofence" : "Add Geofence", isCurrentPage: true },
  ];

  const [ mapCenter, setMapCenter ] = useState({ lat: 20.5937, lng: 78.9629 }) //default india
  const [ markerLoc, setMarkerLoc ] = useState({ })
  const [ isDrawing, setIsDrawing ] = useState(false);
  const [ circle, setCircle ] = useState(null);
  const [ geofenceName, setGeofenceName ] = useState("");
  const [ geofenceArea, setGeofenceArea ] = useState("");
  const [ enableSaveGeofence, setEnableSaveGeoFence ] = useState(false);
  const [ triggerRender, setTriggerRender ] = useState(false);
  const [ infoWindowDetails, setInfoWindowDetails ] = useState({});
  const [ showExistingGeofences, setShowExistingGeofences ] = useState(false);
  const geofenceVids = useRef({});

  const [ geofenceToEdit, setGeofenceToEdit ] = useState({});
  const SelectedVids = useRef([]);
  const location = useLocation();

  const onLoad = (map) => {
    mapRef.current = map;

    if (geofenceId.current) {
      isEdit.current = true;
      if (fromAlerts) {
        getGeofenceAlertToEdit(location.state, geofenceId.current);
      }
      else {
        getGeofenceToEdit(filters.client, geofenceId.current);
      }
    }
  };

  const fitBounds = (data) => {
    let bounds = new window.google.maps.LatLngBounds();
      const degreesPerMeter = 0.000009;
      const radialDistance = data.r * degreesPerMeter;
      const circleBounds = {
        north: data.clat + radialDistance,
        south: data.clat - radialDistance,
        east: data.clong + radialDistance,
        west: data.clong - radialDistance,
      };

      bounds.extend(new window.google.maps.LatLng(circleBounds.north, circleBounds.east));
      bounds.extend(new window.google.maps.LatLng(circleBounds.south, circleBounds.west));
      mapRef.current.fitBounds(bounds);
  }

  const getGeofenceAlertToEdit = (data, geofenceIdx) => {
    if(data){
      setGeofenceToEdit(data);
      setGeofenceName(data.title);
      setGeofenceArea(data.region);
      setMapCenter({ lat: data.clat, lng: data.clong });
      SelectedVids.current = data.vehIdArr;
      geofenceVids.current = data.vehIdArr;
      setTriggerRender((prev) => !prev);
      fitBounds(data);
    }
  }

  const getGeofenceToEdit = async (client, geofence) => {
    try {
      let data = await getGeofencesByClient(client, geofence);
      setGeofenceToEdit(data);
      setGeofenceName(data.title);
      setGeofenceArea(data.region);
      setMapCenter({ lat: data.clat, lng: data.clong });

      fitBounds(data);
    } catch(error) {
      console.error(error);
      Toast({ isClosable: true, description: error || "Something went wrong", position: "bottom", variant: "subtle", duration: 3000 });
    }
  }

  const fetchCurrentLocation = (lat, long) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat: lat, lng: long } }, (res, status) => {
      if (status === "OK") {
        if (res && res.length) {
          let area = res[0].formatted_address;
          area = area.split(",")?.slice(0, -2)?.join();
          setGeofenceArea(area);
        }
      }
    })
  }

  const onCircleComplete = (circle) => {
    setMapCircleRef.current = circle;
    window.google.maps.event.addListener(circle,'radius_changed', function () {
      setCircle({ r: circle.radius, clat: circle.center.lat(), clong: circle.center.lng() });
    })

    window.google.maps.event.addListener(circle,'center_changed', function () {
      fetchCurrentLocation(circle.center.lat(), circle.center.lng());
      setCircle({ r: circle.radius, clat: circle.center.lat(), clong: circle.center.lng() });
    })

    fetchCurrentLocation(circle.center.lat(), circle.center.lng());
    setCircle({ r: circle.radius, clat: circle.center.lat(), clong: circle.center.lng() });
  };

  const autocompleteOnLoad = (ref) => {
    autocompleteRef.current = ref;
  }

  const clearGeofence = () => {
    if (!setMapCircleRef.current) return;

    setMapCircleRef.current.setMap(null);
    setIsDrawing(false);
    setCircle(null);
  }

  const handlePlaceChange = () => {
    const place = autocompleteRef.current.getPlace();
    if (place.geometry) {
      const bounds = new window.google.maps.LatLngBounds();
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      bounds.union(place.geometry.viewport);
      mapRef.current.fitBounds(bounds);
      setMarkerLoc({ lat, lng});
    }
  }

  const saveGeofence = async () => {
    if (!enableSaveGeofence) return;

    let geofenceData = { };

    if (isEdit.current) {
      geofenceData = { clientId: filters.client, idx: geofenceId.current, mts: Date.now(), isEnabled: true, typ: GEOFENCE_TRIGGER_TYPE };
      if (geofenceToEdit.title !== geofenceName) {
        geofenceData.title = geofenceName;
      }

      if (geofenceToEdit.region !== geofenceArea) {
        geofenceData.region = geofenceArea;
      }

      if (editedGeofenceRef.current) {
        if (geofenceToEdit.r !== editedGeofenceRef.current.r) {
          geofenceData.r = editedGeofenceRef.current.r;
        }

        if (geofenceToEdit.clat !== editedGeofenceRef.current.clat || geofenceToEdit.clong !== editedGeofenceRef.current.clong) {
          geofenceData.clat = editedGeofenceRef.current.clat;
          geofenceData.clong = editedGeofenceRef.current.clong;
        }
      }

      updateGeofence(geofenceData, (error) => {
        if (error) {
          console.error(error);
          Toast({ isClosable: true, description: error || "Something went wrong", position: "bottom", variant: "subtle", duration: 3000 });
        } else {
          navigate(PATHS.VIEW_GEOFENCES);
        }
      });
    } else {
      geofenceData = { clientId: filters.client, ts: Date.now(), title: geofenceName, desc: geofenceArea, clat: circle.clat, clong: circle.clong, r: circle.r };
      addNewGeofence(geofenceData, (error) => {
        if (error) {
          console.error(error);
          Toast({ isClosable: true, description: error || "Something went wrong", position: "bottom", variant: "subtle", duration: 3000 });
        } else {
          navigate(PATHS.VIEW_GEOFENCES);
        }
      });
    }
  }

  const saveGeofenceAlert = async () => {
    if (!enableSaveGeofence) return;
    let geofenceData = { mts: Date.now() };

    if (isEdit.current) {
      geofenceData.typ = GEOFENCE_TRIGGER_TYPE;
      if (geofenceId.current && Number(geofenceId.current)) geofenceData.idx = Number(geofenceId.current);

      if (geofenceToEdit.title !== geofenceName) {
        geofenceData.title = geofenceName;
      }
      if (geofenceToEdit.region !== geofenceArea) {
        geofenceData.region = geofenceArea;
      }
      if (editedGeofenceRef.current) {
        if (geofenceToEdit.r !== editedGeofenceRef.current.r) {
          geofenceData.r = editedGeofenceRef.current.r;
        }

        if (geofenceToEdit.clat !== editedGeofenceRef.current.clat || geofenceToEdit.clong !== editedGeofenceRef.current.clong) {
          geofenceData.clat = editedGeofenceRef.current.clat;
          geofenceData.clong = editedGeofenceRef.current.clong;
        }
      }
      if (!isEqual(geofenceVids.current, SelectedVids.current)) {
        geofenceData.vehIdArr = SelectedVids.current;
      }

      if (geofenceData.title || geofenceData.region || geofenceData.r || geofenceData.clat || geofenceData.clong || geofenceData.vehIdArr) {
        updateAlert(geofenceData, (error) => {
          if (error) {
            console.error("Error Updating New Geofence Alert !" , error);
            Toast({ isClosable: true, description: error || "Something went wrong", position: "bottom", variant: "subtle", duration: 3000 });
          }
          else {
            navigate(`${PATHS.SET_ALERTS}?tab=${1}`)
          }
        });
      }
    }
    else {
      geofenceData = { vehIdArr: SelectedVids.current, title: geofenceName, desc: geofenceArea, clat: circle.clat, clong: circle.clong, r: circle.r, ts: Date.now(), typ:GEOFENCE_TRIGGER_TYPE}
      saveAlert(geofenceData, (error) => {
        if (error) {
          console.error("Error Adding New Geofence Alert !" , error);
          Toast({ isClosable: true, description: error || "Something went wrong", position: "bottom", variant: "subtle", duration: 3000 });
        }
        else {
          navigate(`${PATHS.SET_ALERTS}?tab=${1}`)
        }
      })
    }
  }

  useEffect(() => {
    let isGeofenceEdited = false;
    if (!isEdit.current) {
      if (circle && geofenceName && geofenceArea) {
        isGeofenceEdited = true;
      }
    } else {
      if (geofenceName !== geofenceToEdit.title) {
        isGeofenceEdited = true;
      }

      if (geofenceArea !== geofenceToEdit.region) {
        isGeofenceEdited = true;
      }

      if (editedGeofenceRef.current && (geofenceToEdit.clat !== editedGeofenceRef.current.clat || geofenceToEdit.clong !== editedGeofenceRef.current.clong || geofenceToEdit.r !== editedGeofenceRef.current.r)) {
        isGeofenceEdited = true;
      }
    }

    setEnableSaveGeoFence(isGeofenceEdited);
  }, [ circle, geofenceName, geofenceArea, geofenceToEdit ])

  useEffect(() => {
    const from = searchParams.get("fromAlerts");
    if (from) setFromAlerts(true);
    else setFromAlerts(false);
  }, [searchParams, userVehicles])

  const handleCenterChange = () => {
    if (!circleRef.current) return;

    if (geofenceToEdit.clat !== circleRef.current.center.lat() || geofenceToEdit.clong !== circleRef.current.center.lng()) {
      editedGeofenceRef.current = { ...editedGeofenceRef.current, clat: circleRef.current.center.lat(), clong: circleRef.current.center.lng() };
      setEnableSaveGeoFence(true);
    }
  }

  const handleRadiusChange = () => {
    if (!circleRef.current) return;

    if (geofenceToEdit.r !== circleRef.current.radius) {
      editedGeofenceRef.current = { ...editedGeofenceRef.current, r: circleRef.current.radius };
      setEnableSaveGeoFence(true);
    }
  }

  const handleRowSelect = (_, selected) => {
    if (selected && selected.length) {
      SelectedVids.current = selected.map(selection => selection.original.idx);
      if (isEdit.current) setEnableSaveGeoFence(true);
    }
  }

  useEffect(() => {
    if (!allGeofences || !allGeofences.length) {
      getAlerts(GEOFENCE_TRIGGER_TYPE, (err, data) => {
        if(err) console.error("Error fetching geofence alerts", err);
        else setAllGeofences(data);
      });
    }
  }, [ allGeofences ]);

  return (
    <>
      {isLoaded ? <div className="panel-main add-geofence">
        <header>
          <Breadcrumb paths={fromAlerts ? fromAlertBreadcrumbPaths : breadcrumbPaths} />
          { !fromAlerts ? <ClientPicker /> : null }
        </header>
        <div className="geofence-map">
          <GoogleMap
            mapContainerClassName="map-container"
            center={mapCenter}
            zoom={4}
            onLoad={onLoad}
            options={{
              fullscreenControl: false,
              mapTypeControl: false,
              scaleControl: true,
              streetViewControl: false,
              maxZoom: 18,
              gestureHandling: "cooperative",
            }}
          >
            <div className="map-drawing-buttons">
              <div>
                <Checkbox onChange={(e) => setShowExistingGeofences(e.target.checked)} checked={showExistingGeofences} className="view-existing-geofences">
                  View Existing Geofences
                </Checkbox>
              </div>
              {isDrawing ? <button onClick={clearGeofence} className="cancel-drawing">
                Clear
              </button> : null}
              {!isEdit.current && <button onClick={() => setIsDrawing(true)} className={`toggle-drawing ${isDrawing ? "disable-drawing" : ""}`}>
                <GeoFenceIcon />
                Circle
              </button>}
            </div>

            {(isDrawing && !isEdit.current) ? <DrawingManager
              drawingMode={circle ? null : window.google.maps.drawing.OverlayType.CIRCLE}
              options={{
                drawingControlOptions: {
                  drawingModes: [ window.google.maps.drawing.OverlayType.CIRCLE ]
                },
                drawingControl: false,
                circleOptions: {
                  fillColor: "#00A8E270",
                  strokeWeight: 2,
                  strokeColor: "#00A8E2",
                  strokeOpacity: 0.8,
                  clickable: false,
                  editable: true,
                  zIndex: 1,
                }
              }}
              onCircleComplete={onCircleComplete}
            /> : null}

            { (markerLoc.lat && markerLoc.lng) ? <Marker position={markerLoc} /> : null }

            <Autocomplete
              onLoad={autocompleteOnLoad}
              onPlaceChanged={handlePlaceChange}
            >
              <div className="geofence-search-input">
                <SearchIcon />
                <input
                  type="text"
                  placeholder="Search Location"
                />
              </div>
            </Autocomplete>

            { geofenceToEdit.idx ? <Circle
              onLoad={(ref) => circleRef.current = ref}
              radius={editedGeofenceRef.current?.r || geofenceToEdit.r}
              center={{ lat: editedGeofenceRef.current?.clat || geofenceToEdit.clat, lng: editedGeofenceRef.current?.clong || geofenceToEdit.clong }}
              editable={true}
              clickable={false}
              options={{ fillColor: "#00A8E270", strokeWeight: 2, strokeColor: "#00A8E2", strokeOpacity: 0.8, }}
              zIndex={1}
              onCenterChanged={handleCenterChange}
              onRadiusChanged={handleRadiusChange}
            /> : null }
            { (showExistingGeofences && allGeofences && allGeofences.length) ?
              allGeofences.map(geofence => (
                <>
                  <Circle
                    key={geofence.idx}
                    radius={geofence.r}
                    center={{ lat: geofence.clat, lng: geofence.clong }}
                    editable={false}
                    clickable={false}
                    options={{ fillColor: "#00A8E270", strokeWeight: 2, strokeColor: "#00A8E2", strokeOpacity: 0.8, }}
                    zIndex={1}
                    onClick={() => setInfoWindowDetails(geofence)}
                  />
                </>
              ))
              :
              null
            }

            {(showExistingGeofences && infoWindowDetails && infoWindowDetails.idx) ? <InfoWindow
              position={{ lat: infoWindowDetails.clat, lng: infoWindowDetails.clong }}
              options={{ pixelOffset: new window.google.maps.Size(0, -20) }}
              onCloseClick={() => setInfoWindowDetails({})}
            >
              <div className="info-box">
                <label>
                  Title:
                  <p>{infoWindowDetails.title || infoWindowDetails.region || infoWindowDetails.desc || infoWindowDetails.idx || "geofence"}</p>
                </label>
              </div>
            </InfoWindow> : null}
          </GoogleMap>
        </div>

        <div className="geofence-inputs">
          <Input value={geofenceName} onChange={(e) => setGeofenceName(e.target.value)} placeholder="Enter Geo-fence name" className="geofence-name" />
          <Input value={geofenceArea} onChange={(e) => setGeofenceArea(e.target.value)} placeholder="Geo fence description" />

          {
            !fromAlerts
          ? <Button className={`save-geofence ${enableSaveGeofence ? "" : "disbaled-geofence-button"}`} disabled={enableSaveGeofence} onClick={saveGeofence}>
            Save
          </Button> : null
          }
        </div>
        { !fromAlerts ? <p className="geofence-note">This geo-fence will be applied to all the vehicles leased to zomato</p> : null}
        { fromAlerts ? (
            <AlertsVehiclesTable key={triggerRender} headers={ALERT_VEHICLE_TABLE_HEADERS} className={"add-geo-table-cnt"} handleRowSelect={handleRowSelect} selectedRows={SelectedVids.current} />
          ) : null}
        {
          fromAlerts ?
            <div className="btn-container">
              <Button className={`save-geofence-alert ${enableSaveGeofence ? "" : "disbaled-geofence-button"}`} disabled={enableSaveGeofence} onClick={saveGeofenceAlert}>
               Save Geofence Alert
              </Button>
            </div>
          : null
        }
      </div> : null}
    </>
  )
}

export default AddGeofence
